/**
 * Administration → Quality control (1.0.0-rc.8): QC profiles (the shipped ones are read-only: copy one to change it)
 * and the general settings: the default profile, QC for uploads (browser, phone and API) and what happens when a run
 * fails (1.0.0-rc.9: a review task for a role, a group or a person). Watch folders have their own QC setting;
 * workflows have the Run QC step.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { BadgeCheck, Copy, Pencil, Plus, Save, Star, Trash2 } from 'lucide-react';
import {
  QC_CHECK_IDS, QC_CHECK_INFO, QC_GROUPS, QC_GROUP_LABELS, QcChecksSchema, type QcCheckId, type QcChecks, type QcProfileDTO, type QcSettings, type QcThresholdField,
} from '@prism/contracts';
import { api, errorMessage } from '../../api/client';
import { useQcProfiles, useQcSettings, useRoles } from '../../api/hooks';
import { queryKeys } from '../../api/queryKeys';
import { Modal } from '../../components/Modal';
import { TableScroll } from '../../components/TableScroll';
import { QcReviewTaskFields } from '../../components/QcReviewTask';
import { toast } from '../../components/Toasts';

type Draft = { id: string | null; name: string; description: string; checks: QcChecks };

export function QualityControlAdmin() {
  const profiles = useQcProfiles();
  const settings = useQcSettings();
  const qc = useQueryClient();
  const [draft, setDraft] = useState<Draft | null>(null);
  const [busy, setBusy] = useState(false);
  const refresh = async () => {
    await qc.invalidateQueries({ queryKey: queryKeys.qcProfiles() });
    await qc.invalidateQueries({ queryKey: queryKeys.qcSettings() });
  };
  async function run(fn: () => Promise<unknown>, ok: string) {
    setBusy(true);
    try {
      await fn();
      await refresh();
      toast(ok);
      return true;
    } catch (err) {
      toast(errorMessage(err), 'error');
      return false;
    } finally {
      setBusy(false);
    }
  }
  const items = profiles.data?.items ?? [];
  const enabled = (p: QcProfileDTO) => QC_CHECK_IDS.filter((id) => p.checks[id]?.enabled);

  return (
    <div className="page" data-testid="qc-admin">
      <h2>Quality control</h2>
      <p className="lede">
        Operational QC with FFmpeg: format, picture, sound, structure, subtitles and metadata, with timecoded findings. Not decode-level compliance,
        AS-11/DPP or IMF certification, or a photosensitivity test: use Baton or Vidchecker for certified delivery QC. <Link to="/help/quality-control">About quality control</Link>
      </p>

      {settings.data && <GeneralSettings value={settings.data} profiles={items} busy={busy} onSave={(v) => run(() => api('/api/v1/qc/settings', { method: 'PUT', json: v }), 'Quality control settings saved')} />}

      <section className="panel-card" style={{ maxWidth: 1080, marginTop: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <p className="section-label" style={{ margin: 0, flex: 1 }}>Profiles</p>
          <button className="btn sm" onClick={() => setDraft({ id: null, name: '', description: '', checks: QcChecksSchema.parse({}) })} data-testid="qc-add-profile"><Plus size={13} /> Add profile</button>
        </div>
        <TableScroll>
        <table className="table compact" data-testid="qc-profiles">
          <thead><tr><th>Name</th><th>Checks</th><th>Used by</th><th /></tr></thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.id} data-testid="qc-profile-row" data-name={p.name}>
                <td className="wrap" style={{ fontWeight: 550, minWidth: 180 }}>
                  {p.name}
                  {p.builtIn && <span className="pill" style={{ marginLeft: 6 }}>Built in</span>}
                  {p.isDefault && <span className="pill accent" style={{ marginLeft: 6 }} data-testid="qc-default"><Star size={10} style={{ marginRight: 3 }} />Default</span>}
                  {p.description && <div className="faint" style={{ fontSize: 12, fontWeight: 400, whiteSpace: 'normal' }}>{p.description}</div>}
                </td>
                <td className="muted wrap" style={{ minWidth: 200 }}>{enabled(p).length} of {QC_CHECK_IDS.length}: {enabled(p).map((id) => QC_CHECK_INFO[id].label).join(', ') || 'none'}</td>
                <td className="muted wrap">{p.usedBy.watchFolders || p.usedBy.workflows ? [p.usedBy.watchFolders && `${p.usedBy.watchFolders} watch folder${p.usedBy.watchFolders === 1 ? '' : 's'}`, p.usedBy.workflows && `${p.usedBy.workflows} workflow${p.usedBy.workflows === 1 ? '' : 's'}`].filter(Boolean).join(', ') : '—'}</td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  {!p.builtIn && <button className="btn ghost sm" onClick={() => setDraft({ id: p.id, name: p.name, description: p.description, checks: QcChecksSchema.parse(p.checks) })} data-testid="qc-edit-profile"><Pencil size={12} /> Edit</button>}
                  <button className="btn ghost sm" disabled={busy} title="Copy it to make your own" onClick={() => void run(() => api(`/api/v1/qc/profiles/${p.id}/copy`, { method: 'POST', json: {} }), `Copied ${p.name}`)} data-testid="qc-copy-profile"><Copy size={12} /> Copy</button>
                  {!p.builtIn && <button className="btn ghost sm danger" disabled={busy} onClick={() => {
                    if (!window.confirm(`Remove “${p.name}”? Watch folders and workflows that use it check with the default profile instead.`)) return;
                    void run(() => api(`/api/v1/qc/profiles/${p.id}`, { method: 'DELETE' }), `Removed ${p.name}`);
                  }}><Trash2 size={12} /></button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </TableScroll>
      </section>

      {draft && (
        <ProfileEditor draft={draft} busy={busy} onChange={setDraft} onClose={() => setDraft(null)} onSave={async () => {
          const body = { name: draft.name, description: draft.description, checks: draft.checks };
          const ok = await run(() => api(draft.id ? `/api/v1/qc/profiles/${draft.id}` : '/api/v1/qc/profiles', { method: draft.id ? 'PUT' : 'POST', json: body }), `Saved ${draft.name}`);
          if (ok) setDraft(null);
        }} />
      )}
    </div>
  );
}

function GeneralSettings({ value, profiles, busy, onSave }: { value: QcSettings; profiles: QcProfileDTO[]; busy: boolean; onSave: (v: QcSettings) => Promise<boolean> }) {
  const [v, setV] = useState(value);
  const roles = useRoles().data?.items ?? [];
  const dirty = JSON.stringify(v) !== JSON.stringify(value);
  const fallback = profiles.find((p) => p.builtInKey === 'archive');
  return (
    <section className="panel-card" style={{ maxWidth: 1080 }} data-testid="qc-settings">
      <p className="section-label">General settings</p>
      <div className="field-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <div className="field"><label htmlFor="qc-default-profile">Default profile</label>
          <select id="qc-default-profile" className="select" value={v.defaultProfileId ?? ''} onChange={(e) => setV({ ...v, defaultProfileId: e.target.value || null })} data-testid="qc-default-profile">
            <option value="">{fallback ? `${fallback.name} (shipped default)` : 'Archive ingest'}</option>
            {profiles.filter((p) => p.builtInKey !== 'archive').map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <span className="help">Used by watch folders, uploads, workflows and Run QC… when they do not choose a profile.</span>
        </div>
        <div className="field"><label>Uploads</label>
          <label className="toggle-row"><input type="checkbox" checked={v.uploads.enabled} onChange={(e) => setV({ ...v, uploads: { ...v.uploads, enabled: e.target.checked } })} data-testid="qc-uploads" /> Check uploads from the browser and phones</label>
          <select className="select" aria-label="Profile for uploads" value={v.uploads.profileId ?? ''} disabled={!v.uploads.enabled} onChange={(e) => setV({ ...v, uploads: { ...v.uploads, profileId: e.target.value || null } })} data-testid="qc-uploads-profile">
            <option value="">The default profile</option>
            {profiles.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <span className="help">Off by default. Uploads through the API follow the same setting. Watch folders have their own switch.</span>
        </div>
        <div className="field" data-testid="qc-when-fails"><label>When QC fails</label>
          <label className="toggle-row"><input type="checkbox" checked={v.onFailure.notify} onChange={(e) => setV({ ...v, onFailure: { ...v.onFailure, notify: e.target.checked } })} data-testid="qc-notify" /> Tell whoever started it (in PRISM, and by email when email is set up)</label>
          <span className="help" style={{ marginTop: 6 }}>Create a review task: one task per failed run, linked to the asset’s QC tab. A task for a role or group is every member’s; one of them can take it.</span>
          <QcReviewTaskFields idPrefix="qc" value={v.onFailure.task} onChange={(task) => setV({ ...v, onFailure: { ...v.onFailure, task } })} />
          {!v.onFailure.task && roles.some((r) => r.key === 'media_manager') && (
            <button type="button" className="btn ghost sm" style={{ alignSelf: 'flex-start', marginTop: 4 }} data-testid="qc-task-media-managers"
              onClick={() => setV({ ...v, onFailure: { ...v.onFailure, task: { assignee: { type: 'role', key: 'media_manager' }, dueHours: null, onWarnings: false } } })}>Assign review tasks to Media Managers</button>
          )}
          <span className="help">Each watch folder can use this setting, none, or its own. Workflows add their own steps after Run QC.</span>
        </div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button className="btn primary sm" disabled={!dirty || busy} onClick={() => void onSave(v)} data-testid="qc-save-settings"><Save size={13} /> Save settings</button>
      </div>
    </section>
  );
}

function ProfileEditor({ draft, busy, onChange, onClose, onSave }: { draft: Draft; busy: boolean; onChange: (d: Draft) => void; onClose: () => void; onSave: () => void }) {
  const setCheck = (id: QcCheckId, patch: Partial<QcChecks[QcCheckId]>) => onChange({ ...draft, checks: { ...draft.checks, [id]: { ...draft.checks[id], ...patch } } });
  const setThreshold = (id: QcCheckId, key: string, value: unknown) => setCheck(id, { thresholds: { ...draft.checks[id].thresholds, [key]: value } } as never);
  return (
    <Modal title={draft.id ? `Edit ${draft.name}` : 'Add a QC profile'} wide onClose={onClose} actions={<>
      <button className="btn ghost" onClick={onClose}>Cancel</button>
      <button className="btn primary" disabled={busy || !draft.name.trim()} onClick={onSave} data-testid="qc-save-profile"><Save size={14} /> Save</button>
    </>}>
      <div data-testid="qc-profile-editor" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div className="field-row" style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 12 }}>
          <div className="field"><label htmlFor="qc-name">Name</label><input id="qc-name" className="input" value={draft.name} onChange={(e) => onChange({ ...draft, name: e.target.value })} placeholder="e.g. Channel delivery 1080i" data-testid="qc-profile-name" /></div>
          <div className="field"><label htmlFor="qc-desc">Description</label><input id="qc-desc" className="input" value={draft.description} onChange={(e) => onChange({ ...draft, description: e.target.value })} /></div>
        </div>
        <p className="help">Tick the checks to run. An error makes the run fail; warnings alone make it “warnings”. <Link to="/help/quality-control#profiles" onClick={onClose}>What each check does</Link></p>
        {QC_GROUPS.map((g) => (
          <section key={g}>
            <p className="section-label">{QC_GROUP_LABELS[g]}</p>
            <div className="qc-check-grid">
              {QC_CHECK_IDS.filter((id) => QC_CHECK_INFO[id].group === g).map((id) => {
                const info = QC_CHECK_INFO[id];
                const c = draft.checks[id];
                return (
                  <div key={id} className={`qc-check ${c.enabled ? 'on' : ''}`} data-testid="qc-check" data-check={id}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <label className="toggle-row" style={{ flex: 1, fontWeight: 600 }}>
                        <input type="checkbox" checked={c.enabled} onChange={(e) => setCheck(id, { enabled: e.target.checked })} data-testid={`qc-check-${id}`} /> {info.label}
                      </label>
                      <select className="select" style={{ width: 'auto' }} aria-label={`${info.label} severity`} value={c.severity} disabled={!c.enabled} onChange={(e) => setCheck(id, { severity: e.target.value as 'error' | 'warning' })} data-testid={`qc-severity-${id}`}>
                        <option value="error">Error</option><option value="warning">Warning</option>
                      </select>
                    </div>
                    <span className="help" style={{ marginTop: -4 }}>{info.help}</span>
                    {c.enabled && info.fields.length > 0 && (
                      <div className="qc-thresholds">
                        {info.fields.map((f) => <Threshold key={f.key} id={id} f={f} value={(c.thresholds as Record<string, unknown>)[f.key]} onChange={(v) => setThreshold(id, f.key, v)} />)}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        ))}
        <p className="faint" style={{ fontSize: 12 }}><BadgeCheck size={12} /> Picture and sound checks share one decode pass of the file; only the ticked ones are in it.</p>
      </div>
    </Modal>
  );
}

function Threshold({ id, f, value, onChange }: { id: QcCheckId; f: QcThresholdField; value: unknown; onChange: (v: unknown) => void }) {
  const fid = `qc-${id}-${f.key}`;
  const label = <label htmlFor={fid}>{f.label}{f.unit ? ` (${f.unit})` : ''}</label>;
  if (f.type === 'boolean') {
    return <div className="field"><label className="toggle-row"><input id={fid} type="checkbox" checked={Boolean(value)} onChange={(e) => onChange(e.target.checked)} /> {f.label}</label></div>;
  }
  if (f.type === 'select') {
    return <div className="field">{label}<select id={fid} className="select" value={String(value)} onChange={(e) => onChange(e.target.value)}>{f.options!.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}</select></div>;
  }
  if (f.type === 'list-text' || f.type === 'list-number') {
    return <ListInput fid={fid} label={label} help={f.help} value={(value as Array<string | number>) ?? []} numeric={f.type === 'list-number'} onChange={onChange} />;
  }
  if (f.type === 'number') {
    return <div className="field">{label}<input id={fid} className="input" type="number" step="any" value={String(value ?? '')} onChange={(e) => onChange(e.target.value === '' ? 0 : Number(e.target.value))} data-testid={fid} />{f.help && <span className="help">{f.help}</span>}</div>;
  }
  return <div className="field">{label}<input id={fid} className="input" value={String(value ?? '')} onChange={(e) => onChange(e.target.value)} data-testid={fid} />{f.help && <span className="help">{f.help}</span>}</div>;
}

/** A comma-separated list, kept as typed until it is left. */
function ListInput({ fid, label, help, value, numeric, onChange }: { fid: string; label: React.ReactNode; help?: string; value: Array<string | number>; numeric: boolean; onChange: (v: unknown) => void }) {
  const [text, setText] = useState(value.join(', '));
  const parse = (t: string) => t.split(',').map((x) => x.trim()).filter(Boolean).map((x) => (numeric ? Number(x) : x.toLowerCase())).filter((x) => !numeric || Number.isFinite(x));
  return (
    <div className="field">{label}<input id={fid} className="input" value={text} onChange={(e) => { setText(e.target.value); onChange(parse(e.target.value)); }} data-testid={fid} />{help && <span className="help">{help}</span>}</div>
  );
}
